/* eslint-disable @stylistic/js/max-len */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import style from './kinde-phone-input.module.less';
import { useTranslation } from 'react-i18next';
import { Modal, ModalRef, PhoneInput } from '@law-connect/react-components';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import selectors from '../../redux/selectors';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { toast } from 'react-toastify';
import {
  COLLECT_PHONE_NUMBER_COOKIE,
  PREMATTER_ID_COOKIE,
} from '../../constants';
import { actions } from '../../redux/slices';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import api from '../../redux/api';
export const KindePhoneInput: React.FC = () => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationStep, setShowVerificationStep] = useState(false);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const phoneNumberModalRef = useRef<ModalRef>(null);

  const dispatch = useAppDispatch();

  const user = useAppSelector(selectors.user.getUser());

  const sendVerificationCode = useCallback(async () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      toast.error('Invalid phone number.');
      return;
    }

    setIsLoading(true);
    // We want to validate the phone number via the server
    const result = await api.user.checkPhoneAccessibility({
      phoneNumber,
    });

    if (!result.data?.accessible) {
      // Since the phone number is not accessible
      // then we are going to "bypass" the verification step (although not entirely)
      await verifyCodeAndContinue(phoneNumber, result.data?.code);
      setIsLoading(false);
      return;
    }

    try {
      localStorage.setItem('pendingPhoneVerification', phoneNumber);

      const result = await api.user.sendVerificationCode({
        phoneNumber,
      });

      if (result.data?.success) {
        setIsLoading(false);
        setShowVerificationStep(true);
        toast.success('Code sent.');
      } else {
        setIsLoading(false);
        toast.error(result.message || 'Unable to send code. Try again.');
      }
    } catch (error) {
      setIsLoading(false);
      toast.error('Unable to send code. Try again.');
      Sentry.captureException(error);
    }
  }, [phoneNumber]);

  const verifyCodeAndContinue = useCallback(async (phoneNumber?: string, code?: string) => {
    setIsLoading(true);
    const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);
    const storedPhoneNumber = phoneNumber || localStorage.getItem('pendingPhoneVerification');
    const codeToUse = code || verificationCode;

    // Verify the code (in production, you would validate with a real verification service)
    // For demo purposes, we're accepting any 6-digit code
    if (codeToUse.length !== 6) {
      toast.error('Enter a valid 6-digit code.');
      setIsLoading(false);
      return;
    }

    const result = await api.user.validateVerificationCode({
      phoneNumber: storedPhoneNumber,
      verificationCode: codeToUse,
    });

    if (result.data?.success) {
      // Fetch the user as that has the updated phone number
      dispatch(actions.user.fetch());

      toast.success('Phone number verified.');
    } else {
      toast.error(result.message || 'Invalid code. Try again.');
      setIsLoading(false);
      return;
    }

    if (prematterId) {
      try {
        await dispatch(
          actions.prematter.requestQuote({
            id: prematterId,
          })
        ).unwrap();
        toast.success('Quote requested.');
      } catch (error) {
        console.error('error', error);
        toast.error('Unable to request quote. Try again later.');
        Sentry.captureException(error);
        setIsLoading(false);
        return;
      }

      // Clean up storage
      localStorage.removeItem('pendingPhoneVerification');
      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
      setIsLoading(false);

      navigate(`/case/${prematterId}`);
    } else {
      dispatch(actions.user.fetch());

      // Clean up storage
      localStorage.removeItem('pendingPhoneVerification');
      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
      setIsLoading(false);

      navigate('/account');
    }
  }, [verificationCode, navigate, dispatch]);

  const onContinue = useCallback(() => {
    if (showVerificationStep) {
      verifyCodeAndContinue();
    } else {
      sendVerificationCode();
    }
  }, [showVerificationStep, sendVerificationCode, verifyCodeAndContinue]);

  const resendCode = useCallback(() => {
    sendVerificationCode();
  }, [sendVerificationCode]);

  useEffect(() => {
    // Check if we have a pending verification
    const storedPhone = localStorage.getItem('pendingPhoneVerification');
    if (storedPhone) {
      setPhoneNumber(storedPhone);
      setShowVerificationStep(true);
    }
  }, []);

  useEffect(() => {
    if (user?.phone) {
      setPhoneNumber(user.phone);
      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);
      if (prematterId) {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        window.location.href = `/case/${prematterId}`;
      } else {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        window.location.href = '/account';
      }
    }
  }, [user.phone, setPhoneNumber]);

  useEffect(() => {
    phoneNumberModalRef.current?.openModal();
  }, [phoneNumberModalRef]);

  return (
    <Modal
      ref={phoneNumberModalRef}
      title={showVerificationStep ? 'Confirmation code' : 'Phone number'}
      description={
        showVerificationStep
          ? `A six-digit code has been sent to the phone number ending in ${phoneNumber.slice(
            -4
          )}.`
          : 'Enter your phone number for a lawyer to contact.'
      }
      primaryButton={{
        label: 'Continue',
        onClick: onContinue,
        pending: isLoading,
        disabled: isLoading,
      }}
      useAuthStyle={true}
      hideCloseButton={true}
      fullscreen={true}
      footerText={
        showVerificationStep ? (
          <div className={style.resendCode}>
            Didn't receive your code?{' '}
            <button onClick={resendCode} className={style.resendButton}>
              Send again
            </button>
            .
          </div>
        ) : undefined
      }
    >
      {showVerificationStep ? (
        <div className={style.phoneInputContainer}>
          <div className={style.phoneInputLabel}>Confirmation code</div>
          <input
            type='text'
            className={style.phoneInput}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder='Enter 6-digit code'
            maxLength={6}
            disabled={isLoading}
          />
        </div>
      ) : (
        <div className={style.phoneInputContainer}>
          <div className={style.phoneInputLabel}>Phone</div>
          <PhoneInput
            value={phoneNumber}
            onChange={setPhoneNumber}
            className={style.phoneInput}
            disabled={isLoading}
            props={{
              style: {
                backgroundColor: 'var(--white)',
              },
            }}
          />
        </div>
      )}
    </Modal>
  );
};
